import { upperFirst } from 'lodash';
import { request as sonataRequest } from 'sonata-react-components';

import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';
import type { DealPublisherInterface } from 'sonata-static-storage/lib/schemas/premium/dealPublisher.schema';

export async function createDeal(
  dealConfig: Partial<Record<string, any>>,
  userId: string,
  token: Record<string, any>
): Promise<any> {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}deal`,
    {
      data: {
        ...dealConfig,
        userId
      },
      method: 'POST'
    },
    token
  );
}

export async function updateDeal(
  dealConfig: Record<string, any>,
  id: string,
  token: Record<string, any>
): Promise<any> {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}deal/${id}`,
    {
      data: dealConfig,
      method: 'PUT'
    },
    token
  );
}

export function getDealPublisher(
  dealPublisherId: DealInterface['publisher'],
  token: Record<string, any>
): Promise<DealInterface> {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}dealPublisher/${dealPublisherId}`,
    {
      method: 'GET'
    },
    token
  );
}

export function getDealsPublishers(token: Record<string, any>): Promise<{
  page: number;
  pageSize: number;
  items: DealPublisherInterface<string>[];
}> {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}dealPublishers/`,
    {
      method: 'GET',
      params: {
        itemsPerPage: 15,
        limit: 'unlimited',
        order: 'asc',
        page: 1,
        sortBy: 'name'
      }
    },
    token
  );
}

export function getSites(token: Record<string, any>) {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}site`,
    {
      method: 'GET',
      params: {
        limit: 'unlimited'
      }
    },
    token
  );
}

export async function getDeals(token: Record<string, any>): Promise<Record<string, any>> {
  return sonataRequest
    .makeRequest(
      `${taptap.config.sonataBackendUrl}deal`,
      {
        method: 'GET',
        params: {
          filterActive: true,
          limit: 'unlimited',
          order: 'asc',
          page: 1,
          sortBy: 'name'
        }
      },
      token
    )
    .then((response: Record<string, any>) => {
      return response.items;
    });
}

export async function deleteDeal(token: Record<string, any>, id: string): Promise<any> {
  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}deal/${id}`,
    {
      method: 'DELETE'
    },
    token
  );
}

export type DealsFilters = {
  active?: boolean;
  adExchange?: DealInterface['origin']['name'];
  country?: DealInterface['country'];
  search?: string;
  [key: string]: any;
  page: number;
  itemsPerPage: string;
};

export async function getFilteredDeals(
  token: Record<string, any>,
  payload: DealsFilters,
  onlyUserDeals = false
): Promise<{
  currentPage: number;
  items: DealInterface<string>[];
  itemsPerPage: number;
  page: number;
  pageSize: number;
  totalItems: number;
}> {
  const params: Record<string, any> = {
    itemsPerPage: payload.itemsPerPage,
    limit: 'unlimited',
    order: 'asc',
    page: payload.page,
    sortBy: 'name',
    ...Object.entries(payload)
      .filter(([key, value]) => value !== undefined && !['page', 'itemsPerPage'].includes(key))
      .reduce((acc, [key, value]) => {
        if (key === 'search') {
          return {
            ...acc,
            filterString: value
          };
        }
        if (key === 'adExchange') {
          return {
            ...acc,
            filterOrigin: {
              name: value
            }
          };
        }
        return {
          ...acc,
          [`filter${upperFirst(key)}`]: value
        };
      }, {}),
    filterOnlyUserDeals: onlyUserDeals
  };

  return sonataRequest.makeRequest(
    `${taptap.config.sonataBackendUrl}deal`,
    {
      method: 'GET',
      params
    },
    token
  );
}
